import * as React from 'react';

import { useGlobalState } from '../contexts/state/GlobalStateContext';
import { IArea, modelCodeFor, usesDiagenix } from '../types/IArea';
import { ISimulation, OrganixContext, TOrganixMode } from './IOrganixContext';
import { OrganixMap } from './OrganixMap';
import { OrganixProcessor } from './OrganixProcessor';
import { OrganixCanvas } from './OrganixCanvas';
import { GridExporterExcel } from '../GridExporterExcel';
import { IOrganixGridPoint } from '../types/IOrganixGridPoint';
import { OrganixError } from './OrganixError';
// Use OrganixSimulateRemote for the original ORGANIX API; the Forge variant
// is the Lambda-on-Forge deployment we're shipping with.
import { OrganixSimulateRemoteForge } from './OrganixSimulateRemoteForge';
import { DiagenixSimulateRemoteForge } from './DiagenixSimulateRemoteForge';
import { PathogenSimulateRemoteForge, IPathogenResult, IPathogenFailure } from './PathogenSimulateRemoteForge';
import { useToast } from '@longline/aqua-ui/services/Toast/useToast';
import { LayerUtils } from '../util/LayerUtils';
import { ELayerType } from '../types/ELayerType';
import { IDataPoint } from '../types/IDataPoint';
import { ILayer } from '../types/ILayer';
import { BathymetryWarning } from './BathymetryWarning';

interface IProps {
  area: IArea;
  intent: 'simulate' | 'export';
  onClose: () => void;
}

interface IBathymetryWarning {
  oldStructureDepth: number;
  maxDepth: number;
}

// The result grid's key for the engine's "Pathogen XY array". The runtime
// names its output keys itself ("Ammonia XY array" -> ammonia); this one is
// assumed until the ORGANIX API with the pathogen model is published.
const PATHOGEN_FIELD = 'pathogen';

const OrganixManager = (props: IProps) => {
  const globalState = useGlobalState();
  const toaster = useToast();

  // Snapshot the inputs so the user can't change them mid-run. Also fix any
  // flipped lat/lng on the rect.
  const createSimulation = (): ISimulation => {
    const newArea = JSON.parse(JSON.stringify(props.area));
    if(newArea.rect.minLng > newArea.rect.maxLng) {
      const t = newArea.rect.minLng; newArea.rect.minLng = newArea.rect.maxLng; newArea.rect.maxLng = t;
    }
    if(newArea.rect.minLat > newArea.rect.maxLat) {
      const t = newArea.rect.minLat; newArea.rect.minLat = newArea.rect.maxLat; newArea.rect.maxLat = t;
    }
    return {
      project: JSON.parse(JSON.stringify(globalState.project)),
      structures: JSON.parse(JSON.stringify(globalState.structures)),
      area: newArea
    };
  }

  const [mode, setMode] = React.useState<TOrganixMode>('init');
  const [simulation, _] = React.useState(createSimulation());
  const [data, setData] = React.useState<IOrganixGridPoint[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [bathymetryWarning, setBathymetryWarning] = React.useState<IBathymetryWarning | null>(null);

  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  // Manager starts in 'init' until the canvas is mounted, then transitions to
  // 'process' to compute the grid.
  React.useEffect(() => {
    if (canvasRef.current) {
      setMode('process');
    }
  }, [canvasRef.current]);

  const handleProcessingComplete = (processedData: IOrganixGridPoint[]) => {
    setData(processedData);

    // If any structure sits deeper than the bathymetry, clamp structureDepth
    // to maxDepth and warn the user (informational — the run continues; they
    // can cancel if they want).
    let maxDepth = 0;
    let maxStructureDepth = 0;
    let hasStructures = false;
    for (const d of processedData) {
      if (d.depth == null) continue;
      if (d.depth > maxDepth) maxDepth = d.depth;
      if (d.structureIndex && d.depth > maxStructureDepth) {
        hasStructures = true;
        maxStructureDepth = d.depth;
      }
    }
    const oldStructureDepth = simulation.area.structureDepth;
    if (hasStructures && oldStructureDepth > maxDepth) {
      simulation.area.structureDepth = Math.floor(maxDepth);
      setBathymetryWarning({ oldStructureDepth, maxDepth });
    }

    // Branch on intent + simulation kind. Export skips the simulation entirely.
    if (props.intent === 'export') {
      setMode('export');
      GridExporterExcel.run(simulation.project, simulation.area, simulation.structures, processedData);
      globalState.stopOrganix();
      return;
    }
    setMode(usesDiagenix(simulation.area.simulationKind) ? 'simulate_diagenix'
      : simulation.area.simulationKind === 'pathogens' ? 'simulate_pathogens'
      : 'simulate_remote');
  }

  const handleCancel = () => globalState.stopOrganix();
  const handleSimulationError = (msg: string) => {
    setError(msg);
    setMode('error');
  }

  const getDepositionLayers = (output: any): ILayer[] => {
    const pocLayer = LayerUtils.createLayer(ELayerType.DailyPOC, "Daily POC",
      output.grid.map((e: any): IDataPoint => ({ lng: e.longitude, lat: e.latitude, value: [e.pocLoad] })));
    const pomLayer = LayerUtils.createLayer(ELayerType.DailyPOM, "Daily POM",
      output.grid.map((e: any): IDataPoint => ({ lng: e.longitude, lat: e.latitude, value: [e.pomLoad] })));
    pomLayer.visible = false;
    return [pocLayer, pomLayer];
  }

  const getDispersionLayers = (output: any): ILayer[] => {
    const ammoniaLayer = LayerUtils.createLayer(ELayerType.Ammonia, "Ammonia",
      output.grid.map((e: any): IDataPoint => ({ lng: e.longitude, lat: e.latitude, value: [e.ammonia] })));
    const oxygenLayer = LayerUtils.createLayer(ELayerType.Oxygen, "Dissolved oxygen",
      output.grid.map((e: any): IDataPoint => ({ lng: e.longitude, lat: e.latitude, value: [e.oxygen] })));
    oxygenLayer.visible = false;
    return [ammoniaLayer, oxygenLayer];
  }

  const handleCompleteOrganix = (output: any) => {
    let layers: ILayer[] = [];
    switch (simulation.area.simulationKind) {
      case 'deposition':
      case 'depositionDiagenix': layers = getDepositionLayers(output); break;
      case 'dispersion':         layers = getDispersionLayers(output); break;
      case 'pathogens':          // runs per grid; see handleCompletePathogens
        break;
    }
    globalState.addResultLayers(layers, simulation.area.name);
    globalState.stopOrganix();
  }

  const DIAGENESIS_VARIABLE_MAP: Record<string, { layerType: ELayerType, visible: boolean, layerName: string }> = {
    pocConcentration:           { layerType: ELayerType.PocInSediment,           visible: true,   layerName: "POC in sediment" },
    bacteriaConcentration:      { layerType: ELayerType.BacteriaInSediment,      visible: false,  layerName: "Bacteria in sediment" },
    oxygenConcentration:        { layerType: ELayerType.OxygenInSediment,        visible: false,  layerName: "Oxygen in sediment" },
    sulphateConcentration:      { layerType: ELayerType.SulphateInSediment,      visible: false,  layerName: "Sulphate in sediment" },
    ironHydroxideConcentration: { layerType: ELayerType.IronHydroxideInSediment, visible: false,  layerName: "Iron hydroxide in sediment" },
    totalSulphideConcentration: { layerType: ELayerType.TotalSulphideInSediment, visible: true,   layerName: "Total sulphide in sediment" },
    ironConcentration:          { layerType: ELayerType.IronInSediment,          visible: false,  layerName: "Iron in sediment" },
    ironSulphideConcentration:  { layerType: ELayerType.IronSulphideInsediment,  visible: false,  layerName: "Iron sulphide in sediment" },
  };

  const getDiagenesisLayers = (diagenesisOutput: any): ILayer[] => {
    const layers: ILayer[] = [];
    for (const [field, mapping] of Object.entries(DIAGENESIS_VARIABLE_MAP)) {
      const layerData: IDataPoint[] = [];
      for (const entry of diagenesisOutput.grid) {
        if (entry[field] == null) continue;
        layerData.push({ lng: entry.longitude, lat: entry.latitude, value: [entry[field]] });
      }
      if (layerData.length > 0) {
        const layer = LayerUtils.createLayer(mapping.layerType, mapping.layerName, layerData);
        layer.visible = mapping.visible;
        layers.push(layer);
      }
    }
    return layers;
  }

  const handleCompleteDiagenix = (motorganixOutput: any, diagenesisOutput: any) => {
    // The job is submitted as ["motorganix", "diagenesis"], so the deposition
    // result is computed whether or not we use it. Discarding it meant anyone
    // who wanted POC deposition *and* sediment chemistry had to run the same
    // area twice.
    //
    // Two things are deliberately left as they were. The diagenesis layers go
    // first because addLayers focuses newLayers[0]. And the deposition layers
    // arrive hidden, so the map after a diagenesis run looks exactly as it did
    // before -- the data is now there to switch on, rather than competing with
    // the sediment output the user asked for.
    const deposition = motorganixOutput?.grid ? getDepositionLayers(motorganixOutput) : [];
    deposition.forEach(layer => layer.visible = false);

    globalState.addResultLayers([...getDiagenesisLayers(diagenesisOutput), ...deposition], simulation.area.name);
    globalState.stopOrganix();
  }

  const handleCompletePathogens = (results: IPathogenResult[], failures: IPathogenFailure[]) => {
    // One run with a layer per grid, not a run per grid: one SIMULATE is one
    // simulation, and addResultLayers hides earlier runs, so a run per grid
    // would leave only the last grid showing. Each grid's result covers the
    // whole area, so the layers overlap completely; only the first is shown.
    const layers = results.map((r, i) => {
      const layer = LayerUtils.createLayer(ELayerType.Pathogen, `Grid ${r.grid}`,
        r.output.grid.map((e: any): IDataPoint => ({ lng: e.longitude, lat: e.latitude, value: [e[PATHOGEN_FIELD]] })));
      layer.visible = i === 0;
      return layer;
    });
    globalState.addResultLayers(layers, simulation.area.name);
    if (failures.length > 0) {
      toaster.toast("The pathogen simulation failed for " + failures.map(f => `Grid ${f.grid} (${f.message})`).join(", ") + ". The other grids' results were kept.", { dismissable: true });
    }
    globalState.stopOrganix();
  }

  const showWarning = bathymetryWarning != null && (mode === 'simulate_remote' || mode === 'simulate_diagenix' || mode === 'simulate_pathogens');

  return (
    <OrganixContext.Provider value={{
      mode, setMode,
      simulation
    }}>
      <OrganixCanvas ref={canvasRef}/>
      {/* canvasRef.current is populated after the first render of <OrganixCanvas>;
          OrganixMap/Processor are only mounted in non-init modes which means
          we've already passed at least one render cycle. */}
      {mode !== 'init' && canvasRef.current && <OrganixMap canvas={canvasRef.current}/>}
      {mode === 'process' && canvasRef.current && <OrganixProcessor canvas={canvasRef.current} onComplete={handleProcessingComplete}/>}
      {mode === 'simulate_remote' && <OrganixSimulateRemoteForge area={simulation.area} data={data} onCancel={handleCancel} onError={handleSimulationError} onComplete={handleCompleteOrganix}/>}
      {mode === 'simulate_diagenix' && <DiagenixSimulateRemoteForge area={simulation.area} data={data} onCancel={handleCancel} onError={handleSimulationError} onComplete={handleCompleteDiagenix}/>}
      {mode === 'simulate_pathogens' && <PathogenSimulateRemoteForge area={simulation.area} data={data} onCancel={handleCancel} onError={handleSimulationError} onComplete={handleCompletePathogens}/>}
      {mode === 'error' &&<OrganixError onClose={handleCancel} error={error}/>}
      {showWarning && bathymetryWarning && (
        <div style={{ position: 'absolute', bottom: '200px', left: '50%', transform: 'translateX(-50%)', zIndex: 1001, maxWidth: '600px' }}>
          <BathymetryWarning structureDepth={bathymetryWarning.oldStructureDepth} maxDepth={bathymetryWarning.maxDepth}/>
        </div>
      )}
    </OrganixContext.Provider>
  )
}

export { OrganixManager }
